import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import * as pdfjs from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Estilos reutilizables
const dropzoneStyle = (isDragActive) => ({
  border: '2px dashed #007bff',
  borderRadius: '4px',
  padding: '20px',
  textAlign: 'center',
  marginBottom: '20px',
  backgroundColor: isDragActive ? '#f0f8ff' : 'white',
  cursor: 'pointer'
});

const buttonStyle = {
  padding: '8px 15px',
  backgroundColor: '#007bff',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer'
};

const disabledButtonStyle = {
  ...buttonStyle,
  opacity: 0.5,
  cursor: 'not-allowed'
};

const voiceActiveStyle = {
  ...buttonStyle,
  backgroundColor: '#dc3545'
};

const PDFViewer = () => {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageTexts, setPageTexts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [recognitionError, setRecognitionError] = useState('');
  //const [voiceResultReceived, setVoiceResultReceived] = useState(false);
  const canvasRef = useRef(null);
  const recognition = useRef(null);

  // Configurar reconocimiento de voz
  useEffect(() => {
    if ('webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.webkitSpeechRecognition;
      recognition.current = new SpeechRecognition();
      recognition.current.continuous = false;
      recognition.current.interimResults = false;
      recognition.current.lang = 'es-ES';

      recognition.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setSearchTerm(transcript);
       // setVoiceResultReceived(true);
        handleSearch();
      };

      recognition.current.onerror = (event) => {
        setRecognitionError('Error en el reconocimiento de voz');
        setIsListening(false);
      };

      recognition.current.onend = () => {
        setIsListening(false);
      };
    } else {
      setRecognitionError('El navegador no soporta reconocimiento de voz');
    }
  }, []);

  // Limpieza al desmontar
  useEffect(() => {
    return () => {
      if (pdfDoc) {
        pdfDoc.destroy();
      }
    };
  }, [pdfDoc]);

  const processPDF = async (pdf) => {
    const numPages = pdf.numPages;
    const pagePromises = Array.from({ length: numPages }, (_, i) => pdf.getPage(i + 1));
    const pages = await Promise.all(pagePromises);
    
    const textPromises = pages.map(async (page) => {
      const content = await page.getTextContent();
      return removeAccents(content.items.map(item => item.str).join(' '));
    });
    
    return {
      texts: await Promise.all(textPromises),
      numPages
    };
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    const reader = new FileReader();
    
    reader.onload = async () => {
      try {
        setErrorMessage('');
        if (pdfDoc) await pdfDoc.destroy();
        
        const pdf = await pdfjs.getDocument(reader.result).promise;
        const { texts, numPages } = await processPDF(pdf);
        
        setPdfDoc(pdf);
        setNumPages(numPages);
        setPageTexts(texts);
        setCurrentPage(1);
      } catch (error) {
        setErrorMessage('Error al cargar el PDF');
        console.error('Error loading PDF:', error);
      }
    };
    
    reader.readAsArrayBuffer(file);
  }, [pdfDoc]);

  const renderPage = useCallback(async (pageNumber) => {
    if (!pdfDoc || !canvasRef.current) return;

    try {
      const page = await pdfDoc.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
    } catch (error) {
      setErrorMessage('Error al renderizar la página');
      console.error('Render error:', error);
    }
  }, [pdfDoc]);

  useEffect(() => {
    if (pdfDoc) renderPage(currentPage);
  }, [currentPage, pdfDoc, renderPage]);

  const removeAccents = (str) => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  };

  const handleSearch = () => {
    const input = searchTerm.trim();
    if (!input) return;

    setErrorMessage('');
    
    // Búsqueda por número de página
    if (/^\d+$/.test(input)) {
      const pageNumber = parseInt(input, 10);
      if (pageNumber > 0 && pageNumber <= numPages) {
        setCurrentPage(pageNumber);
        return;
      }
    }

    // Búsqueda de texto
    const normalizedSearch = removeAccents(input);
    const foundIndex = pageTexts.findIndex(text => 
      text.includes(normalizedSearch)
    );

    if (foundIndex !== -1) {
      setCurrentPage(foundIndex + 1);
    } else {
      setErrorMessage('No se encontraron coincidencias');
    }
  };
// Nuevos controladores de voz mejorados
  const startListening = () => {
    if (recognition.current && !isListening) {
      setRecognitionError('');
      //setVoiceResultReceived(false);
      setIsListening(true);
      recognition.current.start();
    }
  };

  const stopListening = () => {
    if (recognition.current && isListening) {
      recognition.current.stop();
      setIsListening(false);
      
      // Búsqueda automática al soltar si hay contenido
      //if (!voiceResultReceived && searchTerm.trim()) {
    //    handleSearch();
     // }
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'application/pdf',
    multiple: false
  });

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <div 
        {...getRootProps()}
        style={dropzoneStyle(isDragActive)}
      >
        <input {...getInputProps()} />
        <p>
          {isDragActive
            ? 'Suelta el archivo PDF aquí'
            : 'Arrastra un PDF aquí o haz clic para seleccionar'}
        </p>
      </div>

      {errorMessage && (
        <div style={{ color: 'red', marginBottom: '15px' }}>
          {errorMessage}
        </div>
      )}

      {recognitionError && (
        <div style={{ color: 'red', marginBottom: '15px' }}>
          {recognitionError}
        </div>
      )}

      {pdfDoc && (
        <div>
          <div style={{ marginBottom: '15px', display: 'flex', gap: '10px' }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setErrorMessage('');
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Buscar por página o contenido"
              aria-label="Buscar en el documento"
              style={{ flex: 1, padding: '8px' }}
            />
            <button
              onClick={handleSearch}
              style={buttonStyle}
            >
              Buscar
            </button>
            <button
              onMouseDown={startListening}
              onMouseUp={stopListening}
              onTouchStart={startListening}
              onTouchEnd={stopListening}
              onMouseLeave={stopListening}
              style={isListening ? voiceActiveStyle : buttonStyle}
              disabled={!!recognitionError}
              aria-label={isListening ? 'Detener reconocimiento de voz' : 'Iniciar reconocimiento de voz'}
            >
              {isListening ? '🎤 Escuchando...' : '🎤 Voz'}
            </button>
          </div>

          <div style={{ 
            marginBottom: '15px', 
            display: 'flex', 
            gap: '10px', 
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => setCurrentPage(1)}
              style={currentPage === 1 ? disabledButtonStyle : buttonStyle}
              disabled={currentPage === 1}
            >
              Primero
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              style={currentPage === 1 ? disabledButtonStyle : buttonStyle}
              disabled={currentPage === 1}
            >
              Anterior
            </button>
            <span style={{ padding: '8px', minWidth: '120px', textAlign: 'center' }}>
              Página {currentPage} de {numPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
              style={currentPage === numPages ? disabledButtonStyle : buttonStyle}
              disabled={currentPage === numPages}
            >
              Siguiente
            </button>
            <button
              onClick={() => setCurrentPage(numPages)}
              style={currentPage === numPages ? disabledButtonStyle : buttonStyle}
              disabled={currentPage === numPages}
            >
              Último
            </button>
          </div>

          <canvas 
            ref={canvasRef} 
            style={{ width: '100%', border: '1px solid #ddd' }}
            aria-label={`Página ${currentPage} del documento`}
          />
        </div>
      )}
    </div>
  );
};

export default PDFViewer;